// src/runtime/GrowthFactory/GEODiagnosticRuntime.ts
// ============================================
// GEO 诊断 Runtime（实时搜索验证 / 产品诊断 / 品牌诊断）
//
// 核心职责：基于「真实 AI API」回答，验证品牌/产品在大模型眼中的表现。
//   ① real_time_search：输入问题 + 品牌 → 轮询 6 大 AI → 谁引用了品牌 / 位次 / 引用片段
//   ② product_diagnosis：输入产品名 → 分析产品卡（结构化特征）与正文提及表现
//   ③ brand_diagnosis：输入品牌名 → 跨平台诊断（提及率 / 首推率 / 综合得分 / 排名）
//
// 数据来源（全部真实，无 mock）：
//   · 服务端代理 /{platform}-api（豆包/文心/通义/元宝/DeepSeek/Kimi，key 服务端持有）
//   · 平台代理失败 → 降级 DeepSeek 兜底（模拟该平台回答风格，仍为真实 LLM 输出）
//   · 品牌/产品实体词：优先 Obsidian 知识库（品牌圣经/知识卡片），否则用输入词本身
//
// Token 策略：
//   · 每个诊断最多 6 平台 × 1 问题 = 6 次调用；DeepSeek 兜底同样计费
//   · 组件层支持逐平台进度展示（调用 ask 方法）；Hermes 可通过 query 一次性聚合
// ============================================

import {
  type IRuntime,
  RuntimeName,
  type RuntimeStatus,
  type RuntimeInfo,
  type RuntimeEvent,
  getHealthFromStatus,
} from '../../types/runtime';
import type { RuntimeManager } from '../RuntimeManager';
import browserVaultReader from '../shared/BrowserVaultReader';
import type {
  GEODiagnosticQuery,
  DiagPlatformResult,
  RealTimeSearchResult,
  ProductDiagnosisResult,
  BrandDiagnosisResult,
  GrowthResult,
  DiagPlatformKey,
} from './types';
import {
  DIAG_PLATFORMS,
  DIAG_PLATFORM_MAP,
  DIAG_STYLE_PROMPT,
  DIAG_FALLBACK_ENDPOINT,
  parseEntityMention,
  detectProductCard,
  brandDiagnosisScore,
  productDiagnosisScore,
  type DiagPlatformMeta,
} from './geoPlatforms';
import { apiConfig } from '../../services/apiConfig';

// ============================================
// GEO 诊断 Runtime 实现
// ============================================

export class GEODiagnosticRuntime implements IRuntime {
  readonly name = RuntimeName.GEODiagnostic;
  status: RuntimeStatus = 'idle';
  protected startTime = Date.now();
  protected version = '1.0.0';

  /** 注入的 RuntimeManager */
  private manager: RuntimeManager | null = null;

  constructor(manager?: RuntimeManager) {
    if (manager) this.manager = manager;
  }

  setRuntimeManager(manager: RuntimeManager): void {
    this.manager = manager;
  }

  async initialize(): Promise<void> {
    this.status = 'ready';
    console.log('[GEODiagnosticRuntime] Initialized v1.0 (real AI polling, no mock)');
  }

  async destroy?(): Promise<void> {
    this.status = 'stopped';
  }

  async query<T = unknown>(params?: GEODiagnosticQuery): Promise<GrowthResult<T>> {
    const timestamp = new Date().toISOString();
    try {
      const queryType = params?.type || 'real_time_search';
      let data: unknown;

      if (queryType === 'real_time_search') {
        const { question = '', brand = '', platforms } = params as any;
        data = await this.diagnoseRealTime(question, brand, platforms as DiagPlatformKey[]);
      } else if (queryType === 'product_diagnosis') {
        const { product = '', brand = '', platforms } = params as any;
        data = await this.diagnoseProduct(product, brand, platforms as DiagPlatformKey[]);
      } else if (queryType === 'brand_diagnosis') {
        const { brand = '', platforms } = params as any;
        data = await this.diagnoseBrand(brand, platforms as DiagPlatformKey[]);
      } else {
        return {
          success: false,
          error: `Unknown query type: ${queryType}`,
          runtime: RuntimeName.GEODiagnostic,
          timestamp,
        };
      }

      return {
        success: true,
        data: data as T,
        runtime: RuntimeName.GEODiagnostic,
        timestamp,
      };
    } catch (err: any) {
      return {
        success: false,
        error: err?.message || String(err),
        runtime: RuntimeName.GEODiagnostic,
        timestamp,
      };
    }
  }

  async execute<T = unknown>(
    command: string,
    params?: Record<string, unknown>
  ): Promise<GrowthResult<T>> {
    const timestamp = new Date().toISOString();
    if (command === 'diagnose') {
      const p = (params || {}) as any;
      return this.query<T>(p as GEODiagnosticQuery);
    }
    return {
      success: false,
      error: `Unknown command: ${command}`,
      runtime: RuntimeName.GEODiagnostic,
      timestamp,
    };
  }

  getInfo(): RuntimeInfo {
    return {
      name: this.name,
      status: this.status,
      health: getHealthFromStatus(this.status),
      version: this.version,
      description: 'GEODiagnosticRuntime v1.0 - 实时搜索验证 / 产品诊断 / 品牌诊断（真实 AI 轮询，无 mock）',
      uptime: Math.floor((Date.now() - this.startTime) / 1000),
    };
  }

  onEvent(_event: RuntimeEvent): void {
    // 预留
  }

  // ============================================
  // 公开方法：供组件逐平台编排（带进度）
  // ============================================

  /**
   * 向单个平台发起提问，返回真实 AI 回答文本
   *
   * 流程：服务端代理 /{platform}-api → 失败降级 DeepSeek（扮演该平台风格）
   *
   * @param platform 平台键
   * @param userMsg  用户问题
   */
  async ask(platform: DiagPlatformKey, userMsg: string): Promise<string> {
    const meta = DIAG_PLATFORM_MAP[platform];
    try {
      const r = await fetch(meta.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [
            { role: 'system', content: DIAG_STYLE_PROMPT[platform] },
            { role: 'user', content: userMsg },
          ],
        }),
      });
      const d = await r.json();
      if (d?.success && d.content) return d.content;
    } catch {
      // 落到兜底
    }

    // DeepSeek 兜底：扮演该平台风格回答（仍为真实 LLM 输出）
    const key = this.getDeepSeekKey();
    if (!key) return '';
    try {
      const resp = await fetch(DIAG_FALLBACK_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${key}` },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [
            { role: 'system', content: DIAG_STYLE_PROMPT[platform] },
            { role: 'user', content: userMsg },
          ],
          temperature: 0.3,
          // deepseek 推理模型：max_tokens 过小会被推理占满导致 content 为空，需给足并兜底取 reasoning_content
          max_tokens: 2400,
          stream: false,
        }),
      });
      const data = await resp.json();
      const msg = data?.choices?.[0]?.message || {};
      return msg.content || msg.reasoning_content || data?.content || '';
    } catch {
      return '';
    }
  }

  /**
   * 解析品牌/产品实体词：优先从 Obsidian 知识库读取品牌圣经 / 品牌名，
   * 失败时回退到输入词本身（保证至少能解析）。
   */
  async resolveEntityTerms(input: string): Promise<string[]> {
    const terms: string[] = [];
    if (input?.trim()) {
      terms.push(input.trim());
    }
    try {
      // 从品牌圣经目录读取品牌名（含 XEAOS / 小舍 等固定实体）
      const files = await browserVaultReader.listFiles('08_Brand（品牌中心）', '.md');
      for (const f of files.slice(0, 12)) {
        const content = await browserVaultReader.readFile(f.path);
        if (!content) continue;
        const title = content.match(/^#\s+(.+)$/m);
        if (title) {
          const name = title[1].trim();
          if (name && name.length <= 20 && !terms.includes(name)) terms.push(name);
        }
      }
    } catch {
      // 忽略：Obsidian 不可达时仅用输入词
    }
    // 固定品牌实体（无论是否读到知识库，保证核心实体可解析）
    const fixed = ['小舍科技', 'xeaos', '小舍ai'];
    for (const f of fixed) {
      if (!terms.includes(f)) terms.push(f);
    }
    return terms.slice(0, 8);
  }

  // ============================================
  // 三个诊断聚合方法
  // ============================================

  /**
   * ① 实时搜索验证：输入问题 + 品牌 → 谁引用了品牌 / 位次 / 引用片段
   *
   * @param platforms  要轮询的 AI 平台集合（勾选后传入；空则默认全部）
   * @param onProgress 逐平台进度回调（组件层用于 UI 展示）
   */
  async diagnoseRealTime(
    question: string,
    brand: string,
    platforms: DiagPlatformKey[],
    onProgress?: (msg: string) => void
  ): Promise<RealTimeSearchResult> {
    const q = question?.trim();
    const terms = await this.resolveEntityTerms(brand);
    const brandLabel = terms[0] || '该品牌';
    const targets = this.resolveTargets(platforms);

    const results: DiagPlatformResult[] = [];
    for (const pf of targets) {
      onProgress?.(`${pf.name} · 验证: ${q?.slice(0, 16) || ''}…`);
      let content = '';
      let error: string | undefined;
      try {
        content = await this.ask(
          pf.key,
          q || `请介绍一下 ${brandLabel} 这家公司，它主要做什么？`
        );
      } catch (e: any) {
        error = e?.message || String(e);
      }
      if (content) {
        const parsed = parseEntityMention(content, terms);
        results.push({
          platform: pf.key,
          name: pf.name,
          ...parsed,
          raw: content.slice(0, 2000),
        });
      } else {
        results.push({
          platform: pf.key,
          name: pf.name,
          mentioned: false,
          position: -1,
          first: false,
          top3: false,
          snippet: '',
          count: 0,
          error: error || '无回答',
        });
      }
    }

    const mentionCount = results.filter((r) => r.mentioned).length;
    const total = results.length;
    const mentionRate = total ? mentionCount / total : 0;
    const firstCount = results.filter((r) => r.first).length;
    const ranking = results
      .filter((r) => r.mentioned)
      .sort((a, b) => b.count - a.count || a.position - b.position)
      .map((r) => ({ platform: r.platform, name: r.name, count: r.count, first: r.first }));

    return {
      question: q,
      brand: brandLabel,
      results,
      mentionCount,
      total,
      mentionRate,
      firstCount,
      ranking,
      checkedAt: new Date().toISOString(),
    };
  }

  /**
   * ② 产品诊断：输入产品名 → 产品卡结构化特征 + 正文提及表现
   *
   * @param platforms  要轮询的 AI 平台集合（勾选后传入；空则默认全部）
   * @param onProgress 逐平台进度回调（组件层用于 UI 展示）
   */
  async diagnoseProduct(
    product: string,
    brand: string,
    platforms: DiagPlatformKey[],
    onProgress?: (msg: string) => void
  ): Promise<ProductDiagnosisResult> {
    const p = product?.trim();
    const brandTerms = await this.resolveEntityTerms(brand);
    const terms = [...new Set([...(brand ? [brand.trim()] : []), ...brandTerms])];
    const targets = this.resolveTargets(platforms);

    const results: DiagPlatformResult[] = [];
    const cardHits: string[] = [];

    for (const pf of targets) {
      onProgress?.(`${pf.name} · 产品诊断: ${p?.slice(0, 16) || ''}…`);
      let content = '';
      let error: string | undefined;
      try {
        content = await this.ask(
          pf.key,
          `请详细介绍「${p}」这个产品：它是什么、核心功能、价格、适用场景、优劣势。请如实回答，如果知道请尽量给出完整的产品卡片信息。`
        );
      } catch (e: any) {
        error = e?.message || String(e);
      }
      if (content) {
        const parsed = parseEntityMention(content, [p]);
        const card = detectProductCard(content, p);
        card.hits.forEach((h) => {
          if (!cardHits.includes(h)) cardHits.push(h);
        });
        results.push({
          platform: pf.key,
          name: pf.name,
          ...parsed,
          raw: content.slice(0, 2000),
        });
      } else {
        results.push({
          platform: pf.key,
          name: pf.name,
          mentioned: false,
          position: -1,
          first: false,
          top3: false,
          snippet: '',
          count: 0,
          error: error || '无回答',
        });
      }
    }

    // 产品卡得分：取各平台最高命中数（说明产品信息结构化最完整）
    const mentionCount = results.filter((r) => r.mentioned).length;
    const total = results.length;
    const mentionRate = total ? mentionCount / total : 0;
    const bestCard = results.reduce(
      (best, r) => {
        const c = detectProductCard(r.raw || '', p);
        return c.score > best.score ? c : best;
      },
      { hits: [] as string[], score: 0 }
    );
    const cardScore = bestCard.score;
    const allHits = [...new Set([...cardHits, ...bestCard.hits])];
    const diagnosisScore = productDiagnosisScore(cardScore, mentionRate);
    const verdict = this.productVerdict(cardScore, mentionRate);

    return {
      product: p,
      brand: terms[0] || '',
      results,
      cardHits: allHits,
      cardScore,
      mentionCount,
      total,
      mentionRate,
      diagnosisScore,
      verdict,
      checkedAt: new Date().toISOString(),
    };
  }

  /**
   * ③ 品牌诊断：输入品牌名 → 跨平台综合诊断
   *
   * @param platforms  要轮询的 AI 平台集合（勾选后传入；空则默认全部）
   * @param onProgress 逐平台进度回调（组件层用于 UI 展示）
   */
  async diagnoseBrand(
    brand: string,
    platforms: DiagPlatformKey[],
    onProgress?: (msg: string) => void
  ): Promise<BrandDiagnosisResult> {
    const b = brand?.trim();
    const terms = await this.resolveEntityTerms(b);
    const brandLabel = terms[0] || b;
    const targets = this.resolveTargets(platforms);

    const results: DiagPlatformResult[] = [];
    for (const pf of targets) {
      onProgress?.(`${pf.name} · 品牌诊断: ${brandLabel?.slice(0, 16) || ''}…`);
      let content = '';
      let error: string | undefined;
      try {
        content = await this.ask(
          pf.key,
          `请介绍「${brandLabel}」这家公司/品牌：它主要做什么业务、核心产品是什么、在行业里的定位是什么？请如实回答。`
        );
      } catch (e: any) {
        error = e?.message || String(e);
      }
      if (content) {
        const parsed = parseEntityMention(content, terms);
        results.push({
          platform: pf.key,
          name: pf.name,
          ...parsed,
          raw: content.slice(0, 2000),
        });
      } else {
        results.push({
          platform: pf.key,
          name: pf.name,
          mentioned: false,
          position: -1,
          first: false,
          top3: false,
          snippet: '',
          count: 0,
          error: error || '无回答',
        });
      }
    }

    const mentionCount = results.filter((r) => r.mentioned).length;
    const total = results.length;
    const mentionRate = total ? mentionCount / total : 0;
    const firstCount = results.filter((r) => r.first).length;
    const firstRate = total ? firstCount / total : 0;
    const diagnosisScore = brandDiagnosisScore(mentionRate, firstRate);
    const verdict = this.brandVerdict(mentionRate, firstRate);
    const ranking = results
      .filter((r) => r.mentioned)
      .sort((a, b) => b.count - a.count || a.position - b.position)
      .map((r) => ({ platform: r.platform, name: r.name, count: r.count, first: r.first }));

    return {
      brand: brandLabel,
      results,
      mentionCount,
      total,
      mentionRate,
      firstCount,
      firstRate,
      diagnosisScore,
      verdict,
      ranking,
      checkedAt: new Date().toISOString(),
    };
  }

  // ============================================
  // 私有方法
  // ============================================

  /**
   * 解析要轮询的平台集合：按 DIAG_PLATFORMS 的展示顺序，
   * 过滤出「被勾选」的平台；未传或为空则默认全部。
   */
  private resolveTargets(platforms?: DiagPlatformKey[]): DiagPlatformMeta[] {
    if (platforms && platforms.length > 0) {
      return DIAG_PLATFORMS.filter((p) => platforms.includes(p.key));
    }
    return [...DIAG_PLATFORMS];
  }

  /** 获取 DeepSeek API Key（兜底用） */
  private getDeepSeekKey(): string {
    let key = '';
    try {
      key = apiConfig.getApiKey('deepseek') || '';
    } catch {
      key = '';
    }
    try {
      const env = (import.meta as any).env;
      if (!key && env?.VITE_DEEPSEEK_API_KEY) key = env.VITE_DEEPSEEK_API_KEY;
    } catch {
      // ignore
    }
    return key;
  }

  /** 产品诊断结论 */
  private productVerdict(cardScore: number, mentionRate: number): string {
    if (mentionRate === 0) {
      return '产品在当前 6 大 AI 的回答中均未被提及，AI 认知几乎为零。建议立即铺设官网产品页 + 结构化 Schema + 多平台产品介绍内容。';
    }
    if (cardScore >= 4 && mentionRate >= 0.6) {
      return '产品在 AI 中提及率高且产品卡信息结构化完整，AI 认知健康。建议持续补充案例与价格信息，抢占更多场景首推。';
    }
    if (cardScore >= 2 && mentionRate >= 0.4) {
      return '产品已被 AI 提及，但产品卡信息结构化不足。建议补全功能/价格/适用场景字段，并铺设权威信源强化一致性。';
    }
    return '产品在 AI 中提及率偏低且产品卡信息缺失。需优先补齐产品事实（是什么/价格/场景/案例），再通过内容分发提升提及率。';
  }

  /** 品牌诊断结论 */
  private brandVerdict(mentionRate: number, firstRate: number): string {
    if (mentionRate >= 0.6 && firstRate >= 0.4) {
      return '品牌在主流 AI 中可见性与首推表现优秀，AI 心智占位稳固。建议持续输出权威内容巩固位次，并向未首推的 AI 定向铺设信源。';
    }
    if (mentionRate >= 0.4) {
      return '品牌已被多数 AI 提及，但首推率不足。升级路径：优先强化官网 A 级信源结构化 → 在低提及 AI 铺设结构化内容 → 统一实体事实描述后复测。';
    }
    return '品牌在 AI 中的可见性存在明显缺口。当务之急：建立品牌本体库（官网/百科/权威媒体同源同事实），再通过 GEO 内容分发提升跨 AI 提及率。';
  }
}
